import { useCallback, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useParams, useNavigate } from 'react-router-dom'
import { ScrollArea } from '@/components/ui/scroll-area'

import SignaturePad from './SignaturePad'

import { FeedbackDialog } from '@/components/form/feedback-dialog'
import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'
import FullPageSpinner from '@/components/ui/full-page-sinner'
import { SignatureQueryKey } from '../helpers/signature-query-key'
import { ROUTE_BUILDERS } from '@/config/routes'
import PhotoCaptureWitness from './photo-capture-witness'
import { useCreatePublicWitness, useFinalizePublicWitness } from '../services'
import { useFinalizeWitnessAgreement } from '@/features/agreement/services/useFinalizeWitnessAgreement'

interface CapturedData {
  name: string
  cpf: string
  local: string
  maquina: string
}

export default function AssinarWitness() {
  const [step, setStep] = useState(1)
  const [CPF, setCPF] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const queryClient = useQueryClient()
  const { id_client, slot } = useParams<{ id_client: string; slot: string }>()
  const witnessSlot = slot === '2' ? 2 : 1

  const navigate = useNavigate()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const { mutate: addSignature } = useFinalizePublicWitness()
  const { mutate: createSignature } = useCreatePublicWitness()
  const { mutateAsync: finalizeWitnessAgreement } = useFinalizeWitnessAgreement(id_client || '')
  // topo
  const qKey = useMemo(() => SignatureQueryKey(id_client), [id_client])

  const handlePhotoCaptureComplete = useCallback(
    async (data: CapturedData) => {
      setIsLoading(true)
      setCPF(data.cpf)
      try {
        await new Promise<void>((resolve, reject) => {
          createSignature(
            {
              id_client: id_client || '',
              slot: witnessSlot,
              name: data.name,
              cpf: data.cpf,
              local: data.local,
              maquina: data.maquina
            },
            {
              onSuccess: async () => {
                await queryClient.refetchQueries({ queryKey: qKey, exact: true })
                resolve()
              },
              onError: error => {
                console.error('Erro ao criar assinatura:', error)
                setMessage({ type: 'error', text: 'Falha ao cadastrar informações.' })
                setIsDialogOpen(true)
                reject(error)
              }
            }
          )
        })
        setStep(2)
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [createSignature, id_client, queryClient, qKey, witnessSlot]
  )

  const handleSignatureUser = useCallback(
    async (signatureData: File) => {
      setIsLoading(true)
      try {
        await new Promise<void>((resolve, reject) => {
          addSignature(
            { id_client: id_client || '', slot: witnessSlot, cpf: CPF ?? '', assinatura: signatureData },
            {
              onSuccess: async () => {
                if (id_client) {
                  await finalizeWitnessAgreement()
                }
                setMessage({ type: 'success', text: 'Assinatura adicionada com sucesso!' })
                setIsDialogOpen(true)
                resolve()
              },
              onError: error => {
                console.error('Erro ao adicionar assinatura:', error)
                setMessage({ type: 'error', text: 'Falha ao adicionar assinatura.' })
                setIsDialogOpen(true)
                reject(error)
              }
              // remova onSettled se já usa finally
            }
          )
        })
        setStep(3)
      } catch (e) {
        console.error(e)
      } finally {
        setIsLoading(false)
      }
    },
    [addSignature, CPF, finalizeWitnessAgreement, id_client, witnessSlot]
  )

  useEffect(() => {
    if (step === 3 && id_client) {
      navigate(ROUTE_BUILDERS.minute(id_client), { state: { refetchOnMount: true } })
    }
  }, [step, id_client, navigate])

  return (
    <div className="h-full min-w-screen">
      {step === 1 && (
        <ScrollArea>
          <div className="flex flex-col items-center justify-center min-h-screen p-8 w-full max-w-4xl mx-auto relative">
            <div className="absolute top-4 right-4">
              <Button
                className="bg-primary hover:bg-primary-hover text-primary-foreground"
                onClick={() => navigate(-1)}
                variant="outline"
              >
                Voltar
              </Button>
            </div>
            <PhotoCaptureWitness onComplete={handlePhotoCaptureComplete} />
          </div>
        </ScrollArea>
      )}
      {step === 2 && (
        <>
          <div className="absolute top-4 right-4 z-20">
            <Button
              className="bg-primary hover:bg-primary-hover text-primary-foreground"
              onClick={() => setStep(1)}
              variant="outline"
            >
              Voltar
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center ">
            <SignaturePad onComplete={handleSignatureUser} id_client={id_client || 'default_id'} />
          </div>
        </>
      )}

      {isLoading && <FullPageSpinner />}
      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />
    </div>
  )
}
